// zendesk
import React, { type ReactNode, useEffect, useMemo, useRef } from "react";
import { Script, ScriptStrategy } from "gatsby";
import { useHelpCenterStore } from "@/features/help-center/stores/helpCenterStore";
import {
  useZendeskDomain,
  useZendeskKey,
  useZendeskMessengerToken,
} from "@/hooks/firestore/user/useZendesk";
import { useAuthStore, type AuthState } from "@/features/auth/stores/authStore";
import loadjs from "loadjs";

const { idle, postHydrate } = ScriptStrategy;

const wafChallengeUrl = process.env.GATSBY_WAF_CHALLENGE_JS_URL;
const wafCaptchaApi = process.env.GATSBY_WAF_CAPTCHA_API;
const connectOnPageLoad =
  process.env.GATSBY_ZENDESK_CONNECT_ON_PAGE_LOAD === "true";

// Authenticate messenger as early as possible so the first conversation isn't
// created as an anonymous "Web User". Reads state at call time.
const authenticateMessenger = (
  authStatus: AuthState["status"],
  zendeskMessengerToken: string | null,
) => {
  if (authStatus === "guest") {
    window.zE?.("messenger", "logoutUser");
    return;
  }

  // Authed with token
  if (authStatus === "authenticated" && zendeskMessengerToken) {
    window.zE?.("messenger", "loginUser", (cb: (s: string) => void) =>
      cb(zendeskMessengerToken),
    );
    return;
  }
};

const WithScripts = ({ children }: { children: ReactNode }) => {
  const zendeskScriptRef = useRef<HTMLScriptElement | null>(null);

  const zendeskKey = useZendeskKey();
  const zendeskDomain = useZendeskDomain();
  const zendeskMessengerToken = useZendeskMessengerToken();
  const authStatus = useAuthStore((s) => s.status);

  const zendeskUrl = useMemo(
    () =>
      zendeskKey
        ? `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`
        : "",
    [zendeskKey],
  );

  // this is the only place `zendeskHasLoaded` is set.
  // HelpCenter.tsx checks it before every zE call.
  useEffect(() => {
    if (authStatus === "loading" || !zendeskKey) return;

    const nameWithKey = `${zendeskDomain}-${zendeskKey}`;
    loadjs.reset();
    loadjs(zendeskUrl, nameWithKey, {
      before(_path: string, scriptEl: HTMLScriptElement) {
        window.zE?.("messenger", "logoutUser");

        useHelpCenterStore.getState().resetHelpCenter();

        delete window.zE;
        delete window.zESettings;
        delete window.zEACLoaded;
        delete window.zEWebpackACJsonp;
        delete window.zEmbed;

        if (zendeskScriptRef.current) {
          zendeskScriptRef.current.remove();
        }
        zendeskScriptRef.current = scriptEl;

        window.zESettings = {
          webWidget: {
            zIndex: 950,
            chat: {
              connectOnPageLoad: connectOnPageLoad,
            },
          },
        };
        scriptEl.id = "ze-snippet";
      },
      success: () => {
        authenticateMessenger(authStatus, zendeskMessengerToken);
        useHelpCenterStore.getState().setZendeskHasLoaded(true);
      },
    });
  }, [zendeskDomain, zendeskKey, authStatus]);

  // Re-auth when token lands without a script reload
  useEffect(() => {
    if (window.zE) {
      authenticateMessenger(authStatus, zendeskMessengerToken);
    }
  }, [zendeskMessengerToken, authStatus]);

  return (
    <>
      {children}
      {wafChallengeUrl ? (
        <Script
          id="aws-waf"
          strategy={idle}
          type="text/javascript"
          src={wafChallengeUrl}
        />
      ) : null}
      {wafCaptchaApi ? (
        <Script
          id="aws-waf-api"
          strategy={idle}
          type="text/javascript"
          src={wafCaptchaApi}
        />
      ) : null}
      {/* Defines the <apple-pay-button> custom element, so it loads ahead of
          idle scripts to avoid the deposit button appearing blank */}
      <Script
        id="apple-pay"
        strategy={postHydrate}
        src="https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js"
        crossOrigin="anonymous"
      />
    </>
  );
};

export default WithScripts;
